/**
 * EventRipple-merket: geometrien, og den ENESTE kilden til den.
 *
 * Både React-komponenten og `scripts/build-brand-assets.mjs`, som skriver
 * SVG-filene og ikonene, regner ut formen herfra. Det er derfor det ikke
 * finnes en «animert versjon» og en «statisk versjon» som kan skli fra
 * hverandre: den statiske logoen ER animasjonens siste bilde, bokstavelig
 * talt `ringPath(ring, 1, 1)`.
 *
 * FORMEN. Kameraet flytter seg. Animasjonen begynner på skrå, i perspektiv:
 * en dråpe svever over en flate, faller, treffer, og ringene går utover som
 * ellipser. Så vippes kameraet opp til rett ovenfra. Ellipsene retter seg ut
 * til sirkler, flaten reiser seg til midten, og dråpen ender som prikken i
 * sentrum. Det som blir stående er et symmetrisk merke sett rett ovenfra,
 * som er det et ikon trenger: det tåler sirkulær utklipping, det har ingen
 * tung topp, og det leser på 16 piksler.
 *
 * `tilt` er hele den bevegelsen, fra 0 (på skrå) til 1 (rett ovenfra).
 *
 * STREKEN. Ringene er fylte baner, ikke strøk, fordi SVG ikke kan variere
 * tykkelsen langs et strøk. Hver ring er tung der bølgen kommer fra og
 * tynnes ut mot halen, så formen står stille og ser likevel ut som den
 * beveger seg. Det er også grunnen til at ingenting her bruker
 * `vector-effect="non-scaling-stroke"`: den fryser strektykkelsen mot
 * viewBox-skaleringen, så det samme merket blir en klump på 16 piksler og
 * en hårstrek på 512.
 *
 * INNESTENGNING. Alt som beveger seg fødes og dør inne i merkets egen flate.
 * `SAFE` er den ytterste radien noe får røre, og `maxReach()` er kontrollen
 * som gjør regelen umulig å bryte ved et uhell: den måler gjennom HELE
 * kamerabevegelsen, ikke bare i hvile. Bølgene er allerede gjennomsiktige i
 * det de når `WAVE_RX`, så ingenting berører kanten, og ingenting blir noen
 * gang klippet.
 */

export const SIDE = 48
export const CENTER = SIDE / 2

/** Ytterste radius noe får røre. 2,5 enheters klaring til flatens kant. */
export const SAFE = 21.5
/** Ringenes oversving idet de faller på plass. */
export const OVERSHOOT = 1.05
/** 90 % av halve flaten: det en sirkulær utklipping (avatar) tåler. */
export const AVATAR_SAFE = 21.6

/**
 * Flatens sentrum sett på skrå. Ved tilt 1 har den reist seg til CENTER.
 *
 * Merk at kameravinkelen er slakere enn den var da merket var perspektiv i
 * HVILE. Nå er skråstillingen bare noe animasjonen passerer gjennom, og en
 * ring som er forskjøvet nedover OG bred rekker lenger fra sentrum enn den
 * samme ringen sett rett ovenfra. Porten i asset-bygget fanget nettopp det:
 * 30,8 og 0,36 ga 22,01 mot en trygg radius på 21,5.
 */
const SURFACE_CY_TILTED = 28.5
/** ry/rx sett på skrå. Ved tilt 1 er forholdet 1, altså en sirkel. */
const RATIO = 0.42

/** Bølgenes ytterste radius. Der er de for lengst gjennomsiktige. */
export const WAVE_RX = 18.5
export const WAVE_W = 2.2

export interface Ring {
  /** Radius langs flaten. */
  rx: number
  /** Startvinkel i grader for den tunge enden. */
  a0: number
  /** Hvor mange grader ringen spenner over. */
  sweep: number
  /** Tykkelse ved den tunge enden, og ute i halen. */
  w0: number
  w1: number
  /** Dekkevne i hvile. */
  o: number
}

export interface CutSpec {
  rings: Ring[]
  drop: { r: number; hoverCy: number }
  waves: number
}

/**
 * To snitt av samme form. Et favicon er ikke logoen krympet, det er et eget
 * snitt: færre ringer, tyngre strek. Under 28 piksler slutter den tredje
 * ringen å være en ring og blir en grå kant.
 */
export const CUTS: Record<'full' | 'small', CutSpec> = {
  full: {
    rings: [
      { rx: 7.6, a0: -150, sweep: 300, w0: 4.2, w1: 0.7, o: 1 },
      { rx: 12.8, a0: -120, sweep: 285, w0: 3.5, w1: 0.6, o: 0.62 },
      { rx: 17.6, a0: -95, sweep: 270, w0: 3.0, w1: 0.5, o: 0.34 },
    ],
    drop: { r: 4.3, hoverCy: 14.4 },
    waves: 3,
  },
  small: {
    rings: [
      { rx: 10.4, a0: -150, sweep: 300, w0: 6.6, w1: 1.3, o: 1 },
      { rx: 17.0, a0: -110, sweep: 275, w0: 5.2, w1: 1.0, o: 0.6 },
    ],
    drop: { r: 5.8, hoverCy: 15.0 },
    waves: 2,
  },
}

export type Cut = keyof typeof CUTS

/** Flatens sentrum ved en gitt kameravinkel. */
export function surfaceCy(tilt: number): number {
  return SURFACE_CY_TILTED + (CENTER - SURFACE_CY_TILTED) * tilt
}

/** Ellipsens korte halvakse ved en gitt kameravinkel. Ved tilt 1: en sirkel. */
export function ryFor(rx: number, tilt: number): number {
  return rx * (RATIO + (1 - RATIO) * tilt)
}

/**
 * Én ring som fylt bane, tynnet ut mot halen.
 *
 * Ytterkanten tegnes framover og innerkanten tilbake, forskjøvet langs
 * ellipsens NORMAL. Forskyves den langs radien i stedet, blir tykkelsen feil
 * i endene av en ellipse, og det synes med én gang mens kameraet står skrått.
 *
 * `grow` er ringens vei på plass: rundt 0,45 når den fødes, litt over 1 på
 * oversvinget, 1 når den står stille.
 */
export function ringPath(ring: Ring, tilt: number, grow = 1, samples = 56): string {
  const a = ring.rx * grow
  const b = ryFor(ring.rx, tilt) * grow
  const cy = surfaceCy(tilt)
  const from = (ring.a0 * Math.PI) / 180
  const to = ((ring.a0 + ring.sweep) * Math.PI) / 180

  const outer: string[] = []
  const inner: Array<[number, number]> = []
  let head: [number, number] = [0, 0]

  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    const ang = from + (to - from) * t
    const w = ring.w0 + (ring.w1 - ring.w0) * Math.pow(t, 0.9)
    const ca = Math.cos(ang)
    const sa = Math.sin(ang)

    let nx = b * ca
    let ny = a * sa
    const len = Math.hypot(nx, ny) || 1
    nx /= len
    ny /= len

    const px = CENTER + a * ca
    const py = cy + b * sa
    const ox = px + (nx * w) / 2
    const oy = py + (ny * w) / 2

    inner.push([px - (nx * w) / 2, py - (ny * w) / 2])
    if (i === 0) head = [ox, oy]
    outer.push(`${i === 0 ? 'M' : 'L'}${ox.toFixed(2)} ${oy.toFixed(2)}`)
  }

  const tail = inner[inner.length - 1]
  const half1 = (ring.w1 / 2).toFixed(2)
  const half0 = (ring.w0 / 2).toFixed(2)
  const parts = outer.slice()
  parts.push(`A${half1} ${half1} 0 0 1 ${tail[0].toFixed(2)} ${tail[1].toFixed(2)}`)
  for (let i = inner.length - 2; i >= 0; i--) {
    parts.push(`L${inner[i][0].toFixed(2)} ${inner[i][1].toFixed(2)}`)
  }
  parts.push(`A${half0} ${half0} 0 0 1 ${head[0].toFixed(2)} ${head[1].toFixed(2)}`)
  parts.push('Z')
  return parts.join('')
}

/**
 * Største avstand fra flatens sentrum til noe som tegnes, målt gjennom hele
 * kamerabevegelsen og oversvinget. Dette er kontrollen som holder merket
 * innenfor sin egen flate; asset-bygget stopper hvis den brytes.
 */
export function maxReach(cut: CutSpec): {
  rings: number
  wave: number
  dropTop: number
  dropBottom: number
} {
  let rings = 0
  for (const ring of cut.rings) {
    for (let s = 0; s <= 20; s++) {
      const tilt = s / 20
      const a = ring.rx * OVERSHOOT + ring.w0 / 2
      const b = ryFor(ring.rx, tilt) * OVERSHOOT + ring.w0 / 2
      const dy = surfaceCy(tilt) - CENTER
      for (let i = 0; i <= 180; i++) {
        const ang = (i * Math.PI) / 90
        rings = Math.max(rings, Math.hypot(a * Math.cos(ang), dy + b * Math.sin(ang)))
      }
    }
  }
  // Bølgen måles på samme måte som ringene, ikke med et påslag. En forskjøvet
  // ellipse rekker lengst et sted mellom aksene sine, ikke langs en av dem.
  let wave = 0
  for (let s = 0; s <= 20; s++) {
    const tilt = s / 20
    const a = WAVE_RX + WAVE_W / 2
    const b = ryFor(WAVE_RX, tilt) + WAVE_W / 2
    const dy = surfaceCy(tilt) - CENTER
    for (let i = 0; i <= 180; i++) {
      const ang = (i * Math.PI) / 90
      wave = Math.max(wave, Math.hypot(a * Math.cos(ang), dy + b * Math.sin(ang)))
    }
  }

  return {
    rings,
    wave,
    // dråpen tones inn 6 enheter over svevepunktet, og treffer flaten på skrå
    dropTop: CENTER - (cut.drop.hoverCy - 6 - cut.drop.r),
    dropBottom: surfaceCy(0) + cut.drop.r - CENTER,
  }
}

/** Merket i hvile: kameraet rett ovenfra, ringene på plass. Dette er logoen. */
export function restMarkup(cut: CutSpec, color = 'currentColor'): string {
  const rings = cut.rings
    .map((r) => `<path d="${ringPath(r, 1, 1)}" fill="${color}" opacity="${r.o}"/>`)
    .join('')
  return `${rings}<circle cx="${CENTER}" cy="${CENTER}" r="${cut.drop.r}" fill="${color}"/>`
}
